import { LanguageCode } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { TanstackLanguagesMultiSelect } from '../../form/tanstack/features/Language/TanstackLanguagesMultiSelect';
import { WorkerSkillsField } from './WorkerSkillsField';

const validationSchema = z.object({
  languageCodes: z.array(z.string()),
  isActive: z.boolean(),
  nickname: z.string().min(1, 'Required'),
  skills: z.array(z.object({ value: z.number(), label: z.string() }))
});

type FormValues = z.input<typeof validationSchema>;

interface WorkerData {
  id: number;
  nickname: string;
  isActive: boolean;
  languageCodes: string[];
  skills: Array<{ id: number; name: string }>;
}

interface WorkerEditFormProps {
  worker: WorkerData;
  onSuccess?: () => void;
  className?: string;
}

export const WorkerEditForm = ({ worker, onSuccess, className }: WorkerEditFormProps) => {
  const { mutateAsync: patchWorker } = noddiAsync.usePatch({
    type: URLKeys.patchWorker,
    queryConfig: {
      onSuccess: async () => {
        invalidateQueryExactMatch({ urlKey: URLKeys.getWorker, input: { serviceWorkerId: worker.id } });
        onSuccess?.();
      }
    }
  });

  const defaultValues: FormValues = {
    languageCodes: worker.languageCodes,
    isActive: worker.isActive,
    nickname: worker.nickname,
    skills: worker.skills.map((skill) => ({
      value: skill.id,
      label: skill.name
    }))
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await patchWorker({
        serviceWorkerId: worker.id,
        skillIds: value.skills.map((skill) => skill.value),
        languageCodes: value.languageCodes as LanguageCode[],
        isActive: value.isActive,
        nickname: value.nickname
      });
    }
  });

  return (
    <form.AppForm>
      <form
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField name='languageCodes'>{() => <TanstackLanguagesMultiSelect />}</form.AppField>
        <form.AppField name='skills'>
          {() => <WorkerSkillsField initialSkillIds={worker.skills.map((skill) => skill.id)} />}
        </form.AppField>
        <form.AppField name='nickname'>{(field) => <field.Input label='Nickname' />}</form.AppField>
        <form.AppField name='isActive'>{(field) => <field.Checkbox label='Is active' />}</form.AppField>
        <div className='flex justify-end'>
          <form.SubscribeButton label='Save' />
        </div>
      </form>
    </form.AppForm>
  );
};
